// #3567 -- turns score-api's previewPlaceClassByRules answer into the problems
// a person can fix, one per kind, each with the rides (CNOs) it is about. The
// screens pick the plain words and the fix buttons from `kind` (ADR 0004: say
// what is wrong, why, and put the fix one click away). Pure; no i18n here so
// every i18n key stays static at its call site.
//
// The server reasons are score-api's AUTO_PLACE_SKIP codes.
export const PROBLEM_KINDS = Object.freeze({
  MANUAL_RIDES: "manualRides",
  NOT_FINISHED: "notFinished",
  TIED: "tied",
  JUDGE_PLACE_MISSING: "judgePlaceMissing",
  JUDGE_PLACE_DUPLICATE: "judgePlaceDuplicate",
  ALREADY_PLACED: "alreadyPlaced",
  NO_RIDES: "noRides",
  NO_SCORES: "noScores",
  TOO_LARGE: "tooLarge",
  OTHER: "other",
});

const ref = (r) => ({ rideId: r.rideId, competitorNumber: r.competitorNumber ?? null });

// -> [{ kind, rides: [{ rideId, competitorNumber }] }]. Empty when the class is
// placeable. A not-final class can carry two problems at once (a manual ride to
// join AND a ride with no finished sheet), so this is a list.
export function placeByRulesProblems(preview) {
  if (!preview || preview.placeable) return [];
  const rides = (list) => (Array.isArray(list) ? list.map(ref) : []);
  const tied = rides(preview.tiedRides);
  switch (preview.reason) {
    case "not-final": {
      const manual = rides(preview.manualRides);
      const manualIds = new Set(manual.map((r) => r.rideId));
      const unfinished = rides(preview.blockingRides).filter((r) => !manualIds.has(r.rideId));
      const out = [];
      if (manual.length > 0) out.push({ kind: PROBLEM_KINDS.MANUAL_RIDES, rides: manual });
      if (unfinished.length > 0) out.push({ kind: PROBLEM_KINDS.NOT_FINISHED, rides: unfinished });
      return out.length > 0 ? out : [{ kind: PROBLEM_KINDS.OTHER, rides: [] }];
    }
    case "unbroken-tie":
    case "ride-off-required":
      return [{ kind: PROBLEM_KINDS.TIED, rides: tied }];
    case "judge-place-missing":
      return [{ kind: PROBLEM_KINDS.JUDGE_PLACE_MISSING, rides: tied }];
    case "judge-place-duplicate":
      return [{ kind: PROBLEM_KINDS.JUDGE_PLACE_DUPLICATE, rides: tied }];
    case "already-placed":
      return [{ kind: PROBLEM_KINDS.ALREADY_PLACED, rides: [] }];
    case "no-rides":
      return [{ kind: PROBLEM_KINDS.NO_RIDES, rides: [] }];
    case "no-candidates":
      return [{ kind: PROBLEM_KINDS.NO_SCORES, rides: [] }];
    case "too-large":
      return [{ kind: PROBLEM_KINDS.TOO_LARGE, rides: [] }];
    default:
      return [{ kind: PROBLEM_KINDS.OTHER, rides: [] }];
  }
}

// "CNO 277", "CNO 277 and CNO 437", "CNO 277, CNO 437 and CNO 324".
// `cnoLabel(ride)` supplies each ride's text (it owns the i18n for a ride with
// no CNO); `and` is the translated conjunction.
export function joinCnoList(rides, cnoLabel, and) {
  const labels = rides.map(cnoLabel);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} ${and} ${labels.at(-1)}`;
}

// The body the commit sends back: exactly what the person saw.
export function placingsToConfirm(preview) {
  return (preview?.placings ?? []).map(({ rideId, placing }) => ({ rideId, placing }));
}
